import type { SudokuCellView, SudokuDigit } from "@ziolo/shared";

interface SudokuBoardProps {
  cells: SudokuCellView[];
  flash: { index: number; kind: "correct" | "error" | "hint" } | null;
  validateFlash: { errors: number[]; corrects: number[] } | null;
  onSelect: (index: number) => void;
}

export function SudokuBoard({ cells, flash, validateFlash, onSelect }: SudokuBoardProps) {
  const errorSet = new Set(validateFlash?.errors ?? []);
  const correctSet = new Set(validateFlash?.corrects ?? []);

  return (
    <div className="sudoku-board" role="grid" aria-label="Plansza sudoku">
      {cells.map((cell) => {
        const classes = ["sudoku-cell"];
        if (cell.given) classes.push("sudoku-cell--given");
        else if (cell.value !== 0) classes.push("sudoku-cell--filled");
        if (cell.selected) classes.push("sudoku-cell--selected");
        if (cell.region) classes.push("sudoku-cell--region");
        if (cell.sameNumber === "fixed") classes.push("sudoku-cell--same-given");
        if (cell.sameNumber === "editable") classes.push("sudoku-cell--same-filled");
        if (cell.wrong) classes.push("sudoku-cell--wrong");
        if (flash?.index === cell.index) classes.push(`sudoku-cell--flash-${flash.kind}`);
        if (errorSet.has(cell.index)) classes.push("sudoku-cell--flash-error");
        if (correctSet.has(cell.index)) classes.push("sudoku-cell--flash-correct");

        return (
          <button
            key={cell.index}
            type="button"
            role="gridcell"
            className={classes.join(" ")}
            aria-label={cellLabel(cell.index, cell.value, cell.notes)}
            aria-pressed={cell.selected}
            data-index={cell.index}
            onClick={() => onSelect(cell.index)}
          >
            {cell.value !== 0 ? (
              cell.value
            ) : cell.notes.length > 0 ? (
              <span className="sudoku-notes" aria-hidden="true">
                {([1, 2, 3, 4, 5, 6, 7, 8, 9] as const).map((digit) => (
                  <span key={digit} className="sudoku-notes__digit">
                    {cell.notes.includes(digit as SudokuDigit) ? digit : ""}
                  </span>
                ))}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function cellLabel(index: number, value: number, notes: number[]): string {
  const row = Math.floor(index / 9) + 1;
  const col = (index % 9) + 1;
  if (value !== 0) return `Wiersz ${row}, kolumna ${col}: ${value}`;
  if (notes.length > 0) {
    return `Wiersz ${row}, kolumna ${col}: notatki ${notes.join(", ")}`;
  }
  return `Wiersz ${row}, kolumna ${col}: puste`;
}
